/* screens/support/SupportTicketsPanel.jsx — общий блок «мои обращения»:
   список заявок + создание + переписка в дровере. Используется и авторизованной
   страницей /support, и публичной /help (проп guest → поля имени/почты у гостя). */
import React from 'react';
import { EmptyState } from '../../components/patterns/States.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { PHONE_HINT, PHONE_PLACEHOLDER } from '../../components/patterns/PhoneGate.jsx';
import { hooks } from '../../hooks/index.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import * as api from '../../lib/api/support.js';
import { MessageList, Composer } from './SupportChat.jsx';
import { contactPayload } from './contactPayload.js';

const { useState, useCallback, useEffect, useRef } = React;
const { useFakeQuery } = hooks;

// Статусы ГЛАЗАМИ ПОЛЬЗОВАТЕЛЯ — зеркальны операторским: у оператора open=«Ждут
// ответа» (его ход) → у юзера «На рассмотрении» (ждёт поддержку); оператор
// pending=«Ожидание» (ждём юзера) → у юзера «Нужен ваш ответ» (его ход, accent).
const STATUS = {
  open: { label: 'На рассмотрении', tone: 'neutral' },
  pending: { label: 'Нужен ваш ответ', tone: 'accent' },
  closed: { label: 'Закрыта', tone: 'neutral' },
};
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—');

function TicketDrawer({ mode, ticketId, guest, onClose, onChanged }) {
  const toast = useToast();
  // Телефон спрашиваем ровно по тому же правилу, что шаг оплаты (usePhoneGate):
  // источник один — профиль из /api/auth/me. Заводить второй источник (ручку в
  // поддержке) нельзя: он разошёлся бы с профилем, и обещание «спрашиваем один
  // раз» сломалось бы на ближайшей оплате.
  const { user, refreshUser } = useAuth() || {};
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(mode === 'open');
  const [subject, setSubject] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const bodyRef = useRef(null);
  const phoneRef = useRef(null);
  // Есть номер — не спрашиваем. У гостя аккаунта нет, значит спрашиваем всегда:
  // «помнить» его по токену браузера нельзя — токен опознаёт устройство, а на
  // общем планшете это будет уже другой человек с чужим номером в поле.
  const needPhone = !(user && (user.phone || '').trim());

  const fetchTicket = useCallback(async () => {
    try { setTicket(await api.getMyTicket(ticketId)); }
    catch { toast.show('Не удалось загрузить заявку', 'danger'); }
    finally { setLoading(false); }
  }, [ticketId, toast]);

  useEffect(() => { if (mode === 'open') fetchTicket(); }, [mode, fetchTicket]);
  useEffect(() => {
    if (mode !== 'open') return undefined;
    const id = setInterval(async () => { try { setTicket(await api.getMyTicket(ticketId)); } catch { /* тихо */ } }, 12000);
    return () => clearInterval(id);
  }, [mode, ticketId]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [ticket]);

  // Обработчики ПРОБРАСЫВАЮТ ошибку: композер чистит поле только после успеха,
  // иначе отказ по телефону унёс бы с собой набранный вопрос и вложения.
  const sendNew = async (body, files) => {
    setPhoneErr('');
    try {
      setTicket(await api.createTicket(subject, body, files,
        contactPayload({ guest, name, email, phone })));
      // Номер уехал в профиль — перечитываем пользователя, иначе ближайшая
      // оплата спросит телефон повторно (в контексте он ещё пустой).
      if (user && phone.trim() && refreshUser) { try { await refreshUser(); } catch { /* не критично */ } }
      onChanged && onChanged();
    } catch (e) {
      // Текст отказа приходит с бэка готовым к показу (accounts/phones.py) —
      // своего словаря не заводим, иначе он разойдётся с серверным.
      if (e && e.field === 'contact_phone') {
        setPhoneErr(e.detail || 'Проверьте номер телефона.');
        // Поле живёт в теле дровера, а композер — в футере: без возврата фокуса
        // человек видит красную рамку там, куда не смотрит.
        if (phoneRef.current) phoneRef.current.focus();
      } else toast.show((e && e.detail) || 'Не отправилось', 'danger');
      throw e;
    }
  };
  const sendMsg = async (body, files) => {
    try { setTicket(await api.sendMyMessage(ticketId || (ticket && ticket.id), body, files)); onChanged && onChanged(); }
    catch (e) {
      if (e && e.status === 409) toast.show('Заявка закрыта', 'danger');
      else toast.show((e && e.detail) || 'Не отправилось', 'danger');
      throw e;
    }
  };

  const isNew = mode === 'new' && !ticket;
  const closed = ticket && ticket.status === 'closed';
  const askPhone = isNew && needPhone;
  const phoneMissing = askPhone && !phone.trim();

  return (
    <Drawer open onClose={onClose} size="md" breadcrumbs="Поддержка"
      title={ticket ? (ticket.subject || 'Обращение') : 'Новое обращение'}
      footer={
        closed
          ? <div className="t-caption text-mute" style={{ width: '100%', textAlign: 'center' }}>
              Заявка закрыта. Для нового вопроса создайте обращение.
            </div>
          : isNew
            ? <div style={{ width: '100%' }}>
                <Composer onSend={sendNew} placeholder="Опишите ваш вопрос…"
                  blocked={phoneMissing}
                  blockedHint="Укажите телефон выше — по нему поддержка свяжется с вами." />
              </div>
            : <div style={{ width: '100%' }}><Composer onSend={sendMsg} placeholder="Сообщение…" /></div>
      }>
      {loading ? (
        <div className="center pad-24"><Spinner /></div>
      ) : (
        <div className="stack stack-4">
          {isNew && (
            <div className="stack stack-2">
              <TextInput value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder="Тема (необязательно)" />
              {guest && (
                <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
                  <TextInput value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Ваше имя (необязательно)" style={{ flex: '1 1 160px' }} />
                  <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email для связи (необязательно)" style={{ flex: '1 1 160px' }} />
                </div>
              )}
              {/* Телефон спрашиваем ОДИН РАЗ — только у того, чьего номера у нас
                  ещё нет (у авторизованного он уедет в профиль). Обязательность
                  живёт здесь, у видимого поля, а не в API. */}
              {askPhone && (
                <Field label="Телефон для связи" required error={phoneErr}
                  hint={PHONE_HINT} htmlFor="support-phone">
                  <TextInput id="support-phone" ref={phoneRef} value={phone} inputMode="tel"
                    invalid={!!phoneErr} placeholder={PHONE_PLACEHOLDER}
                    onChange={(e) => { setPhone(e.target.value); if (phoneErr) setPhoneErr(''); }} />
                </Field>
              )}
              {/* Номер авторизованного уходит в ПРОФИЛЬ, а не только в эту
                  заявку. Молчать об этом нельзя: человек может вписать номер
                  управляющего «чтобы перезвонили по этому вопросу», и тот
                  станет постоянным телефоном его аккаунта — включая оплаты.
                  Формулировку берём ту же, что в шаге оплаты (PhoneGate). */}
              {askPhone && user && (
                <p className="t-caption text-mute" style={{ margin: 0 }}>
                  Номер сохранится в вашем профиле: спрашиваем один раз, дальше он
                  подставится сам.
                </p>
              )}
              {/* Согласие показываем ГОСТЮ и ровно там, где он вводит контакты:
                  у авторизованного оно взято при регистрации, а на странице под
                  дровером его в этот момент попросту не видно.
                  Ссылка — НАТИВНАЯ и в новую вкладку, не роутерный Link: тот
                  делает preventDefault + navigate, дровер размонтируется, и
                  человек теряет набранный вопрос вместе с вложениями — ровно
                  за то, что пошёл читать документ, который мы просим прочитать.
                  Тот же приём уже применён в academy/BuyDialog.jsx. */}
              {guest && (
                <p className="t-caption text-mute" style={{ margin: 0 }}>
                  Отправляя обращение, вы соглашаетесь с обработкой персональных данных
                  согласно <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                    className="link">политике конфиденциальности</a>. Контакты нужны только
                  для ответа на обращение.
                </p>
              )}
            </div>
          )}
          <div ref={bodyRef} style={{ maxHeight: '56vh', overflowY: 'auto', paddingRight: 4 }}>
            {ticket
              ? <MessageList messages={ticket.messages} mineSide="user" />
              : <div className="t-caption text-mute center pad-24">
                  Опишите вопрос — команда поддержки ответит прямо здесь.
                </div>}
          </div>
        </div>
      )}
    </Drawer>
  );
}

/** Список заявок + дровер. autoOpenId — открыть конкретную заявку (deep-link из TG). */
function SupportTicketsPanel({ guest = false, autoOpenId = null }) {
  const [nonce, setNonce] = useState(0);
  const [drawer, setDrawer] = useState(null); // {mode:'new'} | {mode:'open', id} | null
  const q = useFakeQuery('my-tickets-' + nonce, () => api.listMyTickets(), [nonce]);
  const items = q.data || [];
  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  const close = () => { setDrawer(null); refresh(); };

  // Deep-link: открыть заявку из ссылки (TG-кнопка → /support?ticket=ID).
  const opened = useRef(null);
  useEffect(() => {
    if (autoOpenId && opened.current !== autoOpenId) {
      opened.current = autoOpenId;
      setDrawer({ mode: 'open', id: Number(autoOpenId) });
    }
  }, [autoOpenId]);

  return (
    <>
      <div className="cluster cluster-8" style={{ marginBottom: 12 }}>
        <Button variant="primary" iconLeft="plus" onClick={() => setDrawer({ mode: 'new' })}>
          Новое обращение
        </Button>
      </div>

      {q.loading ? (
        <div className="center pad-24"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState icon="chat" title="Обращений пока нет"
          body="Создайте обращение — команда поддержки ответит здесь." />
      ) : (
        <div className="stack stack-2">
          {items.map((t) => {
            const st = STATUS[t.status] || { label: t.status, tone: 'neutral' };
            return (
              <button type="button" key={t.id} onClick={() => setDrawer({ mode: 'open', id: t.id })}
                style={{ display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left', width: '100%',
                         padding: '10px 12px', border: '1px solid var(--border, #e5e5e5)', borderRadius: 10,
                         background: t.user_unread ? '#f5f8ff' : '#fff', cursor: 'pointer' }}>
                <span style={{ width: 8, height: 8, borderRadius: 8, flex: '0 0 auto',
                               background: t.user_unread ? '#2f6bff' : 'transparent' }} />
                <span className="grow truncate" style={{ minWidth: 0, fontWeight: 600 }}>
                  {t.subject || 'Обращение'}
                </span>
                <Pill tone={st.tone} size="sm">{st.label}</Pill>
                <span className="t-caption text-mute" style={{ flex: '0 0 auto' }}>
                  {fmtDate(t.last_message_at || t.created_at)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {drawer && (
        <TicketDrawer mode={drawer.mode} ticketId={drawer.id} guest={guest}
          onClose={close} onChanged={refresh} />
      )}
    </>
  );
}

export { SupportTicketsPanel };
